import { Helmet } from 'react-helmet-async';
import { useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import '../styles/jobtread.css';
import reviewsData from '../data/oakandstone-reviews.json';

export const Contact = () => {
  const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
  const isInitialized = useRef(false);

  const banner =
    'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597767/oak-and-stone-remodel/bath/bath3.webp';
  const kitchenRemodel =
    'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597877/oak-and-stone-remodel/show/show14.webp';

  // Function to update file preview UI
  const updateFilePreview = (container, files) => {
    const fileCount = document.getElementById('file-count');
    const fileCountText = document.getElementById('file-count-text');
    
    if (files && files.length > 0) {
      container.classList.add('border-green-500', 'bg-green-50');
      
      // Update file count
      if (fileCount) {
        fileCount.classList.remove('hidden');
        fileCountText.textContent = `${files.length} file${files.length > 1 ? 's' : ''} selected`;
      }
      
      // Clear existing previews
      const existingPreviews = container.querySelectorAll('.file-preview-container');
      existingPreviews.forEach(preview => preview.remove());
      
      // Create file previews in a grid layout
      const previewContainer = document.createElement('div');
      previewContainer.className = 'file-preview-container grid grid-cols-3 gap-2 mt-3 w-full';
      
      Array.from(files).forEach((file, index) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const preview = document.createElement('div');
          preview.className = 'file-preview bg-white rounded-md shadow-md p-2 border border-gray-200 relative';
          preview.innerHTML = `
            <div class="relative">
              <img src="${e.target.result}" alt="${file.name}" class="w-full h-16 object-cover rounded mb-1" />
              <button type="button" class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors" onclick="removeFile(${index})">
                ×
              </button>
            </div>
            <p class="text-xs text-gray-600 truncate" title="${file.name}">${file.name}</p>
          `;
          previewContainer.appendChild(preview);
        };
        reader.readAsDataURL(file);
      });
      
      container.appendChild(previewContainer);
    } else {
      // No files selected
      window.customFileStorage = [];
      container.classList.remove('border-green-500', 'bg-green-50');
      const existingPreviews = container.querySelectorAll('.file-preview-container');
      existingPreviews.forEach(preview => preview.remove());
      
      // Hide file count
      if (fileCount) {
        fileCount.classList.add('hidden');
      }
    }
  };

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;
    // Add global function for file removal
    window.removeFile = (index) => {
      const fileInput = document.getElementById('file-upload');
      if (window.customFileStorage) {
        // Remove file from our custom storage
        window.customFileStorage.splice(parseInt(index), 1);
        
        // Update the file input
        const dt = new DataTransfer();
        window.customFileStorage.forEach(file => dt.items.add(file));
        fileInput.files = dt.files;
        
        // Update the UI directly
        updateFilePreview(fileInput.parentElement, window.customFileStorage);
      }
    };

    // Make updateFilePreview globally available
    window.updateFilePreview = updateFilePreview;

    // Function to send Discord notification
    const sendDiscordNotification = async (formData) => {
      console.log('Starting Discord notification process...');
      
      if (!DISCORD_WEBHOOK_URL) {
        console.error('Discord webhook URL is not defined!');
        throw new Error('Discord webhook URL is not configured');
      }

      try {
        const formObject = Object.fromEntries(formData);
        console.log('Form data collected:', formObject);

        // Get project types with display names
        const projectTypeDisplayNames = [];
        
        // Get the actual selected checkboxes to find their display names
        const selectedCheckboxes = document.querySelectorAll('input[name="account.custom.22P5mvhqehnF"]:checked');
        selectedCheckboxes.forEach(checkbox => {
          const displayName = checkbox.getAttribute('data-display-name');
          if (displayName) {
            projectTypeDisplayNames.push(displayName);
          } else {
            projectTypeDisplayNames.push(checkbox.value);
          }
        });
        
        const projectTypes = projectTypeDisplayNames.join(', ');
        console.log('Project types:', projectTypes);

        // Get uploaded files from form data
        const uploadedFiles = formData.getAll('account.files');
        console.log('Files uploaded:', uploadedFiles.length);

        // Create Discord message
        const discordMessage = {
          content: `\`\`\`***__NEW LEAD FROM WEBSITE__***\`\`\`
**Name**: ${formObject['contact.name']}
**Phone**: ${formObject['contact.custom.22NzPrRDyx7f']}
**Email**: ${formObject['contact.custom.22NzPrQyfhsj']}

**Project Address**: ${formObject['location.address']}
**Project Type**: ${projectTypes}

**Additional Notes**: ${
            formObject['account.custom.22P5muDSNcUh'] ||
            'No additional notes provided'
          }

**Files Uploaded**: ${uploadedFiles.length > 0 ? `${uploadedFiles.length} file(s)` : 'No files uploaded'}
`
        };

        console.log('Sending Discord message:', discordMessage);

        // Send initial message
        const response = await fetch(DISCORD_WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(discordMessage)
        });

        if (!response.ok) {
          throw new Error(
            `Discord API responded with status: ${response.status}`
          );
        }

        console.log('Discord notification sent successfully');

        // Send files if any were uploaded
        if (uploadedFiles.length > 0) {
          console.log('Processing file uploads to Discord...');
          
          for (let i = 0; i < uploadedFiles.length; i++) {
            const file = uploadedFiles[i];
            
            // Check file size (Discord limit is 25MB)
            if (file.size > 25 * 1024 * 1024) {
              console.warn(`File ${file.name} is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Discord limit is 25MB.`);
              continue;
            }

            try {
              // Create FormData for file upload
              const fileFormData = new FormData();
              fileFormData.append('file', file, file.name);

              // Send file to Discord
              const fileResponse = await fetch(DISCORD_WEBHOOK_URL, {
                method: 'POST',
                body: fileFormData
              });

              if (!fileResponse.ok) {
                console.error(`Failed to upload file ${file.name}: ${fileResponse.status}`);
              } else {
                console.log(`Successfully uploaded file: ${file.name}`);
              }

              // Add a small delay between file uploads to avoid rate limiting
              await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (fileError) {
              console.error(`Error uploading file ${file.name}:`, fileError);
            }
          }
        }
        
        return true;
      } catch (error) {
        console.error('Error sending Discord notification:', error);
        throw error;
      }
    };

    // Function to handle form submission
    const handleFormSubmit = async (event) => {
      // Prevent double submission
      if (event.target.dataset.submitting === 'true') {
        console.log('Form already submitting, preventing duplicate submission');
        event.preventDefault();
        return;
      }
      
      console.log('Form submit handler triggered');
      console.log('Processing form submission...');

      // Mark form as submitting
      event.target.dataset.submitting = 'true';
      
      // Show loading state
      const submitButton = event.target.querySelector('[data-submit-button="true"]');
      const originalButtonText = submitButton.innerHTML;
      submitButton.innerHTML = `
        <span class="flex items-center justify-center space-x-2 relative z-10">
          <svg class="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>Submitting...</span>
        </span>
      `;
      submitButton.disabled = true;

      try {
        const formData = new FormData(event.target);
        
        // Add our custom files to the actual file input for JobTread
        if (window.customFileStorage && window.customFileStorage.length > 0) {
          const fileInput = document.getElementById('file-upload');
          if (fileInput) {
            // Create a new FileList-like object
            const dt = new DataTransfer();
            window.customFileStorage.forEach(file => dt.items.add(file));
            fileInput.files = dt.files;
          }
        }
        
        // Send Discord notification
        await sendDiscordNotification(formData);
        
        // Let the form continue with normal JobTread submission
        // Don't prevent default - let JobTread handle the actual submission
        
        // Show success message after a short delay to let JobTread process
        setTimeout(async () => {
          await Swal.fire({
            icon: 'success',
            title: 'Thank You!',
            text: 'Your consultation request has been submitted successfully. We\'ll get back to you within 24 hours!',
            confirmButtonText: 'Great!',
            confirmButtonColor: '#4C5449',
            timer: 5000,
            timerProgressBar: true
          });
        }, 1000);
        
      } catch (error) {
        console.error('Error in form submission handler:', error);
        
        // Show error message
        await Swal.fire({
          icon: 'error',
          title: 'Submission Error',
          text: 'There was an issue submitting your request. Please try again or contact us directly.',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#dc2626'
        });
        
        // Reset form state on error
        event.target.dataset.submitting = 'false';
        submitButton.innerHTML = originalButtonText;
        submitButton.disabled = false;
      }
    };

    // Initialize JobTread form
    const initializeForm = async () => {
      try {
        // Check if script is already loaded
        if (document.querySelector('script[src="https://app.jobtread.com/web-form.js"]')) {
          console.log('JobTread script already loaded');
        } else {
          // Load JobTread script
          const script = document.createElement('script');
          script.src = 'https://app.jobtread.com/web-form.js';
          script.async = true;

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });

          console.log('JobTread script loaded successfully');
        }

        // Find and initialize form
        const form = document.querySelector(
          'form[data-jobtread-web-form="true"]'
        );
        if (!form) {
          console.error('Could not find JobTread form');
          return;
        }

        // Check if event listener is already attached
        if (form.dataset.listenerAttached === 'true') {
          console.log('Event listener already attached');
          return;
        }

        console.log('Found JobTread form, attaching listeners...');

        // Add submit event listener
        form.addEventListener('submit', handleFormSubmit);
        form.dataset.listenerAttached = 'true';

        // Override JobTread's default alert behavior
        const originalAlert = window.alert;
        window.alert = function(message) {
          // Suppress ALL alerts and log them instead
          console.log('Suppressed alert:', message);
          return; // Don't show any alerts
        };

        // Also try to prevent JobTread from showing its own success messages
        // by overriding any potential success callbacks
        if (window.jobtread) {
          window.jobtread.onSuccess = function() {
            console.log('Suppressed JobTread success callback');
            // Don't call the original callback
          };
        }

        // Return cleanup function
        return () => {
          form.removeEventListener('submit', handleFormSubmit);
          form.dataset.listenerAttached = 'false';
          // Restore original alert function
          window.alert = originalAlert;
        };
      } catch (error) {
        console.error('Error initializing JobTread form:', error);
      }
    };

    // Initialize form
    let cleanup;
    initializeForm().then((cleanupFn) => {
      if (cleanupFn) cleanup = cleanupFn;
    });

    // Also override alert globally to catch any delayed alerts
    const globalOriginalAlert = window.alert;
    window.alert = function(message) {
      console.log('Global suppressed alert:', message);
      return; // Don't show any alerts
    };

    // Monitor for any dynamically added scripts that might call alert
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.tagName === 'SCRIPT') {
              // Override alert in any new scripts
              const scriptContent = node.textContent || '';
              if (scriptContent.includes('alert(')) {
                console.log('Detected script with alert calls, overriding...');
                // The global override should catch these
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup function
    return () => {
      if (cleanup) cleanup();
      // Restore global alert function
      window.alert = globalOriginalAlert;
      // Stop the observer
      observer.disconnect();
    };
  }, [DISCORD_WEBHOOK_URL]);

  return (
    <div>
      <Helmet>
        <title>
           Kitchen & Bathroom Remodeling Experts – Ogden, UT
        </title>
        <meta
          name="description"
           content="Get a quote for your home remodel from Oak & Stone. Serving Ogden, Tremonton, and surrounding Utah areas. Custom cabinets, kitchens, and more."
        />
        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Contact Oak & Stone Construction | Kitchen & Bathroom Remodel Experts"
        />
        <meta
          property="og:description"
          content="Ready to start your home remodeling project? Contact Oak & Stone Construction for expert kitchen and bathroom remodeling in Weber, Davis, and Box Elder counties. Get a free consultation today!"
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597767/oak-and-stone-remodel/bath/bath3.webp"
        />
        <meta
          property="og:url"
          content="https://oakandstoneremodel.com/contact"
        />
        <meta property="og:type" content="website" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Contact Oak & Stone Construction | Kitchen & Bathroom Remodel Experts"
        />
        <meta
          name="twitter:description"
          content="Ready to start your home remodeling project? Contact Oak & Stone Construction for expert kitchen and bathroom remodeling in Weber, Davis, and Box Elder counties. Get a free consultation today!"
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597767/oak-and-stone-remodel/bath/bath3.webp"
        />
        <meta
          name="twitter:url"
          content="https://oakandstoneremodel.com/contact"
        />
        <link rel="canonical" href="https://oakandstoneremodel.com/contact" />
      </Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Contact Oak & Stone Construction | Kitchen & Bathroom Remodel Experts',
          url: 'https://oakandstoneremodel.com/contact',
          description:
            'Ready to start your home remodeling project? Contact Oak & Stone Construction for expert kitchen and bathroom remodeling in Weber, Davis, and Box Elder counties. Get a free consultation today!'
        })}
      </script>

             {/* Hero Section with Background Image */}
       <div className="relative min-h-screen">
         {/* Background Image */}
         <div className="absolute inset-0 z-0">
        <img
          src={banner}
             className="object-cover w-full h-full"
             alt="Beautiful kitchen remodel background"
        />
           <div className="absolute inset-0 bg-black bg-opacity-60" />
         </div>



                   {/* Two Column Layout */}
          <div className="relative z-10 flex items-start justify-center min-h-screen pt-20 pb-8">
            <div className="w-full max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                
                {/* Left Column - Testimonials (1/3 width) */}
                <div className="lg:col-span-1 text-white order-2 lg:order-1">
                  <h1 className="text-3xl font-bold mb-4">
                    Ready to Transform Your Home?
                  </h1>
                  <p className="text-lg text-white/90 mb-6 leading-relaxed">
                    Join our satisfied customers who have trusted Oak & Stone Construction 
                    to bring their remodeling dreams to life.
                  </p>

                                     {/* Scrollable Testimonials */}
                   <div 
                     className="h-96 overflow-y-auto pr-4 pl-2 space-y-4 py-2 testimonials-scroll" 
                     style={{ 
                       scrollbarWidth: 'none', 
                       msOverflowStyle: 'none'
                     }}
                   >
                     <style dangerouslySetInnerHTML={{
                       __html: `
                         .testimonials-scroll::-webkit-scrollbar {
                           display: none;
                         }
                       `
                     }} />
                     {reviewsData.reviews.map((review, index) => (
                       <a 
                         key={index} 
                         href="https://www.google.com/search?q=Oak+and+Stone+Remodel+Ogden+UT+reviews" 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="block bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 transition-all duration-200 hover:bg-white/20 hover:border-white/30 hover:shadow-lg transform hover:scale-[1.01] cursor-pointer group relative z-10"
                       >
                         <div className="flex items-center space-x-2 mb-3">
                           <div className="flex items-center space-x-1">
                             {[...Array(review.rating)].map((_, i) => (
                               <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                               </svg>
                             ))}
                           </div>
                           <span className="text-white/70 text-sm">{review.stars}</span>
                           <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                             <svg className="w-4 h-4 text-white/70" fill="currentColor" viewBox="0 0 20 20">
                               <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                             </svg>
                           </div>
                         </div>
                         <p className="text-white/90 text-sm leading-relaxed mb-3">
                           "{review.review_text}"
                         </p>
                         <div className="text-white font-semibold text-sm">
                           — {review.customer_name}
                         </div>
                       </a>
                     ))}
                   </div>
                </div>

                                 {/* Right Column - Contact Form (2/3 width) */}
                 <div className="lg:col-span-2 flex justify-center order-1 lg:order-2">
                   <div className="w-full max-w-4xl">
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                      {/* Compact Form Header */}
                      <div className="bg-gradient-to-r from-[#4C5449] to-[#5a6358] p-4 text-center">
                        <div className="flex items-center justify-center space-x-3 mb-2">
                        
                          <div>
                            <h2 className="text-2xl font-bold text-white">Oak and Stone Construction</h2>
                            <p className="text-white/80 text-sm mt-3">Contact us today — we can help with your remodel and provide a quote</p>
                          </div>
                        </div>
                 
        </div>


                                              {/* Compact Contact Form */}
                       <div className="p-4">
                         <form
                           className="jtwf space-y-3"
                           data-jobtread-web-form="true"
                           data-key="22SzWPYHhnuNrqbY9UbUkmG389gAvSXnxy"
                           method="POST">
                           <input type="hidden" name="account.create_job" value="false" />
                           
                           {/* Contact Information Section */}
                           <div className="space-y-2">
                             <div className="grid grid-cols-3 gap-3">
                               <div className="space-y-1 group">
                                 <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide group-hover:text-primary transition-colors duration-200">
                                   Name<span className="ml-1 text-red-500">*</span>
                                 </label>
                                 <input
                                   className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 transition-all duration-200 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white text-sm hover:border-gray-300 hover:bg-gray-100"
                                   type="text"
                                   required=""
                                   name="contact.name"
                                   placeholder="Full name"
                                 />
                               </div>
                               <div className="space-y-1 group">
                                 <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide group-hover:text-primary transition-colors duration-200">
                                   Phone<span className="ml-1 text-red-500">*</span>
                                 </label>
                                 <input
                                   className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 transition-all duration-200 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white text-sm hover:border-gray-300 hover:bg-gray-100"
                                   type="tel"
                                   required=""
                                   name="contact.custom.22NzPrRDyx7f"
                                   placeholder="(123) 456-7890"
                                 />
                               </div>
                               <div className="space-y-1 group">
                                 <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide group-hover:text-primary transition-colors duration-200">
                                   Email<span className="ml-1 text-red-500">*</span>
                                 </label>
                                 <input
                                   className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 transition-all duration-200 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white text-sm hover:border-gray-300 hover:bg-gray-100"
                                   type="email"
                                   required=""
                                   name="contact.custom.22NzPrQyfhsj"
                                   placeholder="your@email.com"
                                 />
                               </div>
                             </div>
                           </div>

                           {/* Project Details Section */}
                           <div className="space-y-2">
                             
                             {/* Project Address */}
                             <div className="space-y-1 group">
                               <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide group-hover:text-primary transition-colors duration-200">
                                 Project Address<span className="ml-1 text-red-500">*</span>
                               </label>
                               <textarea
                                 className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 outline-none focus:bg-white min-h-[35px] resize-none text-sm hover:border-gray-300 hover:bg-gray-100"
                                 name="location.address"
                                 required=""
                                 placeholder="Complete address where work will be performed"></textarea>
                             </div>

                             {/* Project Type - Organized Grid */}
                             <div className="space-y-2">
                               <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide">
                                 Project Type
                               </label>
                                                                <div className="grid grid-cols-3 gap-1 p-2 bg-gray-50 rounded-lg border border-gray-100">
                                 {[
                                   { display: 'Kitchen', value: 'Kitchen' },
                                   { display: 'Bathroom', value: 'Bathroom' },
                                   { display: 'Custom Cabinets', value: 'Kitchen' },
                                   { display: 'Cabinet Refinish', value: 'Kitchen' },
                                   { display: 'Countertop Replacement', value: 'Kitchen' },
                                   { display: 'Flooring', value: 'Flooring' },
                                   { display: 'Full House', value: 'Full House' },
                                   { display: 'Basement', value: 'Basement' },
                                   { display: 'Other', value: 'Other' }
                                 ].map((type, index) => (
                                   <label
                                     key={`${type.display}-${index}`}
                                     className="flex items-start p-1.5 space-x-1.5 rounded-md transition-all duration-200 hover:bg-white hover:shadow-md border border-transparent hover:border-primary/20 cursor-pointer group">
                                     <input
                                       className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-2 focus:ring-primary/20 mt-0.5 flex-shrink-0 transition-all duration-200"
                                       type="checkbox"
                                       name="account.custom.22P5mvhqehnF"
                                       value={type.value}
                                       data-display-name={type.display}
                                     />
                                     <span className="text-gray-700 text-xs font-medium group-hover:text-primary leading-tight transition-colors duration-200">{type.display}</span>
                                   </label>
                                 ))}
                               </div>
                             </div>

                             {/* File Upload & Notes - Side by side */}
                             <div className="grid grid-cols-2 gap-3">
                               <div className="space-y-1 group">
                                 <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide group-hover:text-primary transition-colors duration-200">
                                   Upload Photos (Optional)
                                 </label>
                                 <div 
                                   className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center hover:border-primary hover:bg-primary/5 transition-all duration-200 min-h-[80px] flex flex-col items-center justify-center group-hover:shadow-sm relative"
                                   onDragOver={(e) => {
                                     e.preventDefault();
                                     e.currentTarget.classList.add('border-primary', 'bg-primary/10');
                                   }}
                                   onDragLeave={(e) => {
                                     e.preventDefault();
                                     e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                                   }}
                                   onDrop={(e) => {
                                     e.preventDefault();
                                     e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                                     const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
                                     if (files.length > 0) {
                                       const fileInput = document.getElementById('file-upload');
                                       if (fileInput) {
                                         // Get existing files from our custom storage
                                         const existingFiles = window.customFileStorage || [];
                                         
                                         // Add new files (limit to 10 total files)
                                         const maxFiles = 10;
                                         const filesToAdd = files.slice(0, maxFiles - existingFiles.length);
                                         
                                         if (filesToAdd.length < files.length) {
                                           Swal.fire({
                                             icon: 'warning',
                                             title: 'File Limit Reached',
                                             text: `Only ${filesToAdd.length} of ${files.length} files were added. Maximum ${maxFiles} files allowed.`,
                                             confirmButtonText: 'OK',
                                             confirmButtonColor: '#4C5449'
                                           });
                                         }
                                         
                                         // Store the combined files in our custom storage
                                         window.customFileStorage = [...existingFiles, ...filesToAdd];
                                         
                                         // Update the file input with the combined files
                                         const dt = new DataTransfer();
                                         window.customFileStorage.forEach(file => dt.items.add(file));
                                         fileInput.files = dt.files;
                                         
                                         // Update the UI directly without triggering onChange
                                         updateFilePreview(fileInput.parentElement, window.customFileStorage);
                                       }
                                     } else {
                                       Swal.fire({
                                         icon: 'warning',
                                         title: 'Invalid File Type',
                                         text: 'Please drop image files only.',
                                         confirmButtonText: 'OK',
                                         confirmButtonColor: '#4C5449'
                                       });
                                     }
                                   }}
                                 >
                                   <input
                                     className="hidden"
                                     type="file"
                                     accept="image/*"
                                     name="account.files"
                                     id="file-upload"
                                     data-multiple="true"
                                     onChange={(e) => {
                                       const newFiles = Array.from(e.target.files);
                                       const container = e.target.parentElement;
                                       
                                       // For file input changes, replace all files (don't combine with existing)
                                       const allFiles = [...newFiles];
                                       
                                       // Validate file count and size
                                       const maxFiles = 10;
                                       const maxSize = 10 * 1024 * 1024; // 10MB
                                       
                                       if (allFiles.length > 0) {
                                         // Check file count
                                         if (allFiles.length > maxFiles) {
                                           Swal.fire({
                                             icon: 'warning',
                                             title: 'File Limit Reached',
                                             text: `Maximum ${maxFiles} files allowed. Only the first ${maxFiles} files will be uploaded.`,
                                             confirmButtonText: 'OK',
                                             confirmButtonColor: '#4C5449'
                                           });
                                           // Keep only the first maxFiles
                                           allFiles.splice(maxFiles);
                                         }
                                         
                                         // Check file sizes
                                         const oversizedFiles = allFiles.filter(file => file.size > maxSize);
                                         if (oversizedFiles.length > 0) {
                                           const fileNames = oversizedFiles.map(f => f.name).join(', ');
                                           Swal.fire({
                                             icon: 'warning',
                                             title: 'File Too Large',
                                             text: `The following files are too large (max 10MB each): ${fileNames}`,
                                             confirmButtonText: 'OK',
                                             confirmButtonColor: '#4C5449'
                                           });
                                           // Remove oversized files
                                           allFiles.splice(allFiles.findIndex(file => file.size > maxSize), 1);
                                         }
                                         
                                         // Store the validated files in our custom storage
                                         window.customFileStorage = allFiles;
                                         
                                         // Update the UI using the shared function
                                         updateFilePreview(container, allFiles);
                                       } else {
                                         // No files selected
                                         window.customFileStorage = [];
                                         updateFilePreview(container, []);
                                       }
                                     }}
                                   />
                                   
                                   <label htmlFor="file-upload" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                                     <svg className="mx-auto h-5 w-5 text-gray-400 group-hover:text-primary transition-colors duration-200" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                       <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                     </svg>
                                     <p className="text-xs text-gray-600 mt-1 group-hover:text-primary transition-colors duration-200">
                                       <span className="font-medium">Click to upload</span>
                                       <br />
                                       <span className="text-gray-500">or drag & drop an image</span>
                                     </p>
                                   </label>
                                 </div>
                                 
                                 {/* File count indicator */}
                                 <div id="file-count" className="text-xs text-gray-500 mt-1 hidden">
                                   <span id="file-count-text">0 files selected</span>
                                 </div>

                               </div>

                               <div className="space-y-1 group">
                                 <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide group-hover:text-primary transition-colors duration-200">
                                   Additional Details
                                 </label>
                                 <textarea
                                   className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 outline-none focus:bg-white min-h-[80px] resize-none text-sm hover:border-gray-300 hover:bg-gray-100"
                                   name="account.custom.22P5muDSNcUh"
                                   placeholder="Vision, timeline, budget, requirements..."></textarea>
                               </div>
                             </div>
                           </div>

                           {/* Submit Button */}
                           <div className="text-center pt-3">
                             <button
                               className="w-full px-6 py-3 font-semibold text-white bg-gradient-to-r from-primary to-primary-hover rounded-lg shadow-md transition-all duration-300 hover:shadow-xl transform hover:scale-[1.02] text-sm relative overflow-hidden group"
                               data-submit-button="true">
                               <span className="flex items-center justify-center space-x-2 relative z-10">
                                 <svg className="w-4 h-4 group-hover:rotate-12 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                 </svg>
                                 <span>Request My Free Consultation</span>
                               </span>
                               <div className="absolute inset-0 bg-gradient-to-r from-primary-hover to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                             </button>
                             <p className="text-xs text-gray-500 mt-2 flex items-center justify-center">
                               <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                               </svg>
                               We'll get back to you within 24 hours
                             </p>
                           </div>
                         </form>
                       </div>
                    </div>
                  </div>
                </div>
             </div>
           </div>
         </div>
       </div>

      {/* Content Section Below Form */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                Let's Build Something Amazing Together
              </h2>
              <div className="w-20 h-1 bg-primary mb-8" />
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We're excited to collaborate on your home improvement project! 
                Fill out the form above and we'll get back to you within 24 hours with 
                a personalized consultation tailored to your vision.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary text-xl">✓</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Free Consultation & Estimate</h3>
                    <p className="text-gray-600">Get a detailed estimate and professional consultation at no cost to you.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary text-xl">🎨</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Expert Project Guidance</h3>
                    <p className="text-gray-600">Our team will walk you through options and help you plan your remodel with confidence.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary text-xl">🏆</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Quality Craftsmanship Guarantee</h3>
                    <p className="text-gray-600">We stand behind our work with a comprehensive quality guarantee.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div>
              <img
                src={kitchenRemodel}
                alt="Beautiful kitchen remodel by Oak and Stone"
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-gray-800 mb-6">
            Serving Weber, Davis & Box Elder Counties
        </h3>
          <p className="text-lg text-gray-600 mb-12 leading-relaxed">
            Oak and Stone Construction proudly serves neighborhoods throughout Utah's 
            northern counties. With a strong reputation for exceptional craftsmanship 
            and satisfied clients, we specialize in bringing your vision to life. 
            From bathroom remodels and kitchen upgrades to full home renovations, 
            our team is ready to transform your home into something truly special.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="font-semibold text-gray-800 mb-2">Weber County</h4>
              <p className="text-gray-600 text-sm">Ogden, Roy, Clearfield, Layton, and surrounding areas</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="font-semibold text-gray-800 mb-2">Davis County</h4>
              <p className="text-gray-600 text-sm">Farmington, Kaysville, Bountiful, and surrounding areas</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h4 className="font-semibold text-gray-800 mb-2">Box Elder County</h4>
              <p className="text-gray-600 text-sm">Brigham City, Tremonton, and surrounding areas</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
